import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from "@material-ui/icons";
import { AuthContext } from "../../../context/Auth/AuthContext";
import api from "../../../services/api";

const ANSI_REGEX = new RegExp(`${String.fromCharCode(27)}\\[[0-9;]*m`, "g");

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 600,
    maxWidth: 900
  },
  progressBar: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    height: 8,
    borderRadius: 4
  },
  logsContainer: {
    backgroundColor: theme.palette.type === 'dark' ? "#1e1e1e" : "#f5f5f5",
    borderRadius: 8,
    padding: theme.spacing(2),
    maxHeight: "60vh",
    overflow: "auto",
    fontFamily: "monospace",
    fontSize: "0.875rem",
    marginTop: theme.spacing(2)
  },
  logEntry: {
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.6,
    wordBreak: "break-word"
  },
  logInfo: {
    color: theme.palette.type === 'dark' ? "#4fc3f7" : "#1976d2"
  },
  logSuccess: {
    color: "#4caf50"
  },
  logError: {
    color: "#f44336"
  },
  logWarning: {
    color: "#ff9800"
  },
  statusText: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary
  },
  tabContainer: {
    marginTop: theme.spacing(2)
  }
}));

const UpdateSystemProgress = ({ open, onClose, companyId, userId }) => {
  const classes = useStyles();
  const { socket } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("running"); // running, completed, error
  const [currentTab, setCurrentTab] = useState(0);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const completionDetectedRef = useRef(false); // Flag para evitar múltiplas detecções
  const logsEndRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isRunningRef = useRef(false);
  const lastLogTimeRef = useRef(null);
  const completionTimeoutRef = useRef(null);

  // Scroll automático para o final dos logs
  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs]);

  useEffect(() => {
    if (!open) {
      // Resetar flags quando fechar o modal
      completionDetectedRef.current = false;
      return;
    }
    
    // Resetar flags quando abrir o modal
    completionDetectedRef.current = false;

    // Função para verificar status via API (quando socket desconecta)
    const checkStatus = async () => {
      try {
        const { data } = await api.get("/update-system/status");
        if (data.data) {
          const statusData = data.data;
          setProgress(statusData.progress || 0);
          
          if (statusData.status === "completed" && !completionDetectedRef.current) {
            console.log("[UPDATE SYSTEM] ✅✅✅ Status completed recebido via API");
            console.log("[UPDATE SYSTEM] ✅✅✅ Mostrando popup IMEDIATAMENTE via API (sem delay)");
            completionDetectedRef.current = true; // Marcar como detectado
            isRunningRef.current = false;
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setStatus("completed");
            setProgress(statusData.progress || 95); // Usar progresso do status ou 95%
            // ✅ MOSTRAR POPUP IMEDIATAMENTE - SEM DELAY
            setShowCompletionDialog(true);
            return; // Sair da função para evitar processamento adicional
          } else if (statusData.status === "error") {
            setStatus("error");
            isRunningRef.current = false;
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          } else if (statusData.status === "running") {
            isRunningRef.current = true;
          }
        } else {
          // Se não há status, pode ter sido limpo (concluído há mais de 1 minuto)
          // Verificar se já estava em progresso há muito tempo
          if (isRunningRef.current && !completionDetectedRef.current) {
            // Se estava rodando mas não há mais status, assumir que concluiu
            // (o status é limpo 1 minuto após conclusão)
            console.log("[UPDATE SYSTEM] ✅ Status completed assumido (sem status há muito tempo)");
            console.log("[UPDATE SYSTEM] ✅ Mostrando popup de conclusão (fallback)");
            completionDetectedRef.current = true; // Marcar como detectado
            setStatus("completed");
            setProgress(100);
            isRunningRef.current = false;
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            // Mostrar popup de conclusão
            setShowCompletionDialog(true);
          }
        }
      } catch (error) {
        // Se houver erro de rede, mas estava rodando, pode ter concluído
        if (isRunningRef.current && error.code !== 'ERR_NETWORK' && !error.message?.includes('Network')) {
          console.log("[UPDATE SYSTEM] Erro ao verificar status:", error);
        } else if (isRunningRef.current) {
          // Se estava rodando e há erro de rede, pode ter concluído durante a desconexão
          // Verificar novamente após um tempo
          console.log("[UPDATE SYSTEM] Erro de rede ao verificar status, tentando novamente...");
        }
      }
    };

    // Verificar status imediatamente ao abrir (caso já esteja concluído)
    checkStatus();

    if (!socket) {
      // Se não há socket, iniciar polling imediatamente
      pollingIntervalRef.current = setInterval(checkStatus, 3000);
      return;
    }


    // Função para remover códigos ANSI de cor das mensagens
    const removeAnsiCodes = (text) => {
      if (!text) return text;
      // Remove códigos ANSI como [1;37m, [0m, etc.
      return text.replace(ANSI_REGEX, '').replace(/\[[0-9;]*m/g, '');
    };

    // Função para detectar conclusão baseada nos logs
    const detectCompletionFromLogs = (newLogs) => {
      // Verificar se há mensagem específica do atualizador_remoto_FAST.sh
      // A mensagem exata é: "Atualização Concluida, Reiniciando Instancias e Aplicando a Atualização..."
      // Pode conter códigos ANSI como [1;37m
      const successMessages = [
        "atualização concluida",
        "atualização concluída",
        "atualização concluida, reiniciando instancias",
        "reiniciando instancias e aplicando a atualização",
        "atualização do sistema concluída com sucesso",
        "atualização concluída com sucesso"
      ];
      
      // Verificar se há mensagem de erro também
      const errorMessages = [
        "erro ao atualizar",
        "falha ao atualizar",
        "❌ erro",
        "error",
        "failed"
      ];
      
      const hasSuccess = newLogs.some(log => {
        let message = (log.message || log || '').toString();
        // Remover códigos ANSI antes de verificar
        message = removeAnsiCodes(message).toLowerCase();
        // Procurar por qualquer uma das mensagens de sucesso
        return successMessages.some(success => message.includes(success));
      });
      
      const hasError = newLogs.some(log => {
        let message = (log.message || log || '').toString();
        // Remover códigos ANSI antes de verificar
        message = removeAnsiCodes(message).toLowerCase();
        return errorMessages.some(error => message.includes(error));
      });
      
      // Só considerar concluído se tiver mensagem explícita de sucesso E não tiver erro
      if (hasSuccess && !hasError && isRunningRef.current && !completionDetectedRef.current) {
        const foundLog = newLogs.find(log => {
          let msg = (log.message || log || '').toString();
          msg = removeAnsiCodes(msg).toLowerCase();
          return successMessages.some(s => msg.includes(s));
        });
        console.log("[UPDATE SYSTEM] ✅✅✅ DETECÇÃO DE CONCLUSÃO IMEDIATA - Mensagem encontrada:", foundLog);
        console.log("[UPDATE SYSTEM] ✅✅✅ Mostrando popup IMEDIATAMENTE (sem delay)");
        
        // ✅ MARCAR COMO DETECTADO IMEDIATAMENTE (antes de qualquer outra coisa)
        completionDetectedRef.current = true;
        
        // ✅ PARAR TUDO IMEDIATAMENTE
        isRunningRef.current = false;
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
          completionTimeoutRef.current = null;
        }
        
        // ✅ ATUALIZAR ESTADO E MOSTRAR POPUP IMEDIATAMENTE (sem setTimeout)
        setStatus("completed");
        setProgress(95); // Progresso quando detecta a mensagem (antes do PM2 reiniciar)
        
        // ✅ MOSTRAR POPUP IMEDIATAMENTE - SEM DELAY
        setShowCompletionDialog(true);
        
        return true;
      }
      return false;
    };

    // Listener para logs
    const onLog = (log) => {
      // Log de debug para ver o que está sendo recebido
      const logMessage = (log.message || log || '').toString();
      const logMessageLower = logMessage.toLowerCase();
      
      // Logar TODOS os logs que contenham palavras-chave relacionadas
      if (logMessageLower.includes('atualização') || 
          logMessageLower.includes('concluida') || 
          logMessageLower.includes('concluída') ||
          logMessageLower.includes('reiniciando') ||
          logMessageLower.includes('instancias')) {
        console.log("[UPDATE SYSTEM] 📝 Log recebido (possível conclusão):", logMessage);
        console.log("[UPDATE SYSTEM] 📝 Log limpo (sem ANSI):", removeAnsiCodes(logMessage));
      }
      
      setLogs(prev => {
        const newLogs = [...prev, log];
        lastLogTimeRef.current = Date.now();
        
        // Verificar se há mensagem de conclusão nos logs IMEDIATAMENTE
        const detected = detectCompletionFromLogs(newLogs);
        if (detected) {
          console.log("[UPDATE SYSTEM] ✅✅✅ Conclusão detectada via onLog! ✅✅✅");
        }
        
        return newLogs;
      });
    };

    // Listener para progresso
    const onProgress = (data) => {
      console.log("[UPDATE SYSTEM] onProgress recebido:", data);
      if (data.progress !== undefined) {
        setProgress(data.progress);
      }
      if (data.status) {
        setStatus(data.status);
        
        // Se completou ou teve erro, parar polling
        if (data.status === "completed" || data.status === "error") {
          console.log("[UPDATE SYSTEM] Status completed/error recebido via socket:", data.status);
          isRunningRef.current = false;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // ✅ Se completou, mostrar popup IMEDIATAMENTE (sem delay)
          if (data.status === "completed" && !completionDetectedRef.current) {
            console.log("[UPDATE SYSTEM] ✅✅✅ Mostrando popup IMEDIATAMENTE via socket (sem delay)");
            completionDetectedRef.current = true; // Marcar como detectado
            
            if (completionTimeoutRef.current) {
              clearTimeout(completionTimeoutRef.current);
              completionTimeoutRef.current = null;
            }
            
            // ✅ MOSTRAR POPUP IMEDIATAMENTE - SEM DELAY, SEM TIMEOUT
            setShowCompletionDialog(true);
            
            // Não fazer checkStatus aqui para não atrasar - já temos confirmação via socket
          }
        } else if (data.status === "running") {
          isRunningRef.current = true;
        }
      }
    };

    // Listener para reconexão do socket
    const onReconnect = () => {
      console.log("[UPDATE SYSTEM] Socket reconectado, verificando status...");
      // Verificar status imediatamente após reconexão
      checkStatus();
      // Iniciar polling se ainda estiver rodando
      if (isRunningRef.current && !pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(checkStatus, 3000); // Verificar a cada 3 segundos (mais frequente)
      }
    };

    // Listener para desconexão do socket
    const onDisconnect = () => {
      console.log("[UPDATE SYSTEM] Socket desconectado, iniciando polling...");
      // Quando socket desconectar, iniciar polling para verificar status
      if (isRunningRef.current && !pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(checkStatus, 3000); // Verificar a cada 3 segundos (mais frequente)
      }
    };

    socket.on("updateSystemLog", onLog);
    socket.on("updateSystemProgress", onProgress);
    socket.on("connect", onReconnect);
    socket.on("disconnect", onDisconnect);

    // Verificar status inicial
    checkStatus();

    // Função para verificar conclusão baseada nos logs periodicamente
    const checkLogsForCompletion = () => {
      if (!isRunningRef.current) return;
      
      // Usar uma função de callback para acessar os logs atuais
      setLogs(currentLogs => {
        // Função para remover códigos ANSI de cor das mensagens
        const removeAnsiCodes = (text) => {
          if (!text) return text;
          // Remove códigos ANSI como [1;37m, [0m, etc.
          return text.replace(ANSI_REGEX, '').replace(/\[[0-9;]*m/g, '');
        };

        // Verificar se há mensagem específica do atualizador_remoto_FAST.sh
        // A mensagem exata é: "Atualização Concluida, Reiniciando Instancias e Aplicando a Atualização..."
        // Pode conter códigos ANSI como [1;37m
        const successMessages = [
          "atualização concluida",
          "atualização concluída",
          "atualização concluida, reiniciando instancias",
          "reiniciando instancias e aplicando a atualização",
          "atualização do sistema concluída com sucesso",
          "atualização concluída com sucesso"
        ];
        
        // Verificar se há mensagem de erro também
        const errorMessages = [
          "erro ao atualizar",
          "falha ao atualizar",
          "❌ erro",
          "error",
          "failed"
        ];
        
        const hasSuccess = currentLogs.some(log => {
          let message = (log.message || log || '').toString();
          // Remover códigos ANSI antes de verificar
          message = removeAnsiCodes(message).toLowerCase();
          return successMessages.some(success => message.includes(success));
        });
        
        const hasError = currentLogs.some(log => {
          let message = (log.message || log || '').toString();
          // Remover códigos ANSI antes de verificar
          message = removeAnsiCodes(message).toLowerCase();
          return errorMessages.some(error => message.includes(error));
        });
        
        // Só considerar concluído se tiver mensagem explícita de sucesso E não tiver erro
        if (hasSuccess && !hasError && isRunningRef.current && !completionDetectedRef.current) {
          console.log("[UPDATE SYSTEM] ✅✅✅ Detecção de conclusão via polling - Mostrando popup IMEDIATAMENTE");
          completionDetectedRef.current = true; // Marcar como detectado
          isRunningRef.current = false;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (completionTimeoutRef.current) {
            clearTimeout(completionTimeoutRef.current);
            completionTimeoutRef.current = null;
          }
          setStatus("completed");
          setProgress(95); // Progresso quando detecta a mensagem
          // ✅ MOSTRAR POPUP IMEDIATAMENTE - SEM DELAY
          setShowCompletionDialog(true);
          return currentLogs; // Retornar logs sem modificar
        }
        
        // Remover timeout baseado em inatividade - muito arriscado
        // Melhor esperar mensagem explícita ou timeout de segurança geral
        
        return currentLogs; // Retornar logs sem modificar
      });
    };

    // Iniciar polling como fallback (caso socket não funcione)
    // Usar intervalo menor para detectar conclusão mais rapidamente
    pollingIntervalRef.current = setInterval(() => {
      checkStatus();
      checkLogsForCompletion(); // Verificar logs também
    }, 5000); // Verificar a cada 5 segundos como fallback

    return () => {
      socket.off("updateSystemLog", onLog);
      socket.off("updateSystemProgress", onProgress);
      socket.off("connect", onReconnect);
      socket.off("disconnect", onDisconnect);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
      isRunningRef.current = false;
    };
  }, [open, socket, companyId, userId, onClose, progress]);

  const handleClose = () => {
    if (status === "running") {
      // Não permitir fechar enquanto está rodando
      return;
    }
    onClose();
  };

  // Função para limpar cache e recarregar a página (simula CTRL+R)
  const handleReloadWithCacheClear = () => {
    // Limpar cache do navegador (Service Workers Cache API)
    if ('caches' in window) {
      caches.keys().then(names => {
        return Promise.all(
          names.map(name => caches.delete(name))
        );
      }).catch(() => {
        // Ignorar erros ao limpar cache
      });
    }
    
    // Recarregar a página com bypass de cache (equivalente a CTRL+R)
    // Usar múltiplos métodos para garantir compatibilidade
    try {
      // Método 1: reload com timestamp na URL (força bypass de cache)
      const url = new URL(window.location.href);
      url.searchParams.set('_reload', Date.now().toString());
      window.location.href = url.toString();
    } catch (e) {
      // Método 2: Fallback - reload tradicional (ainda funciona na maioria dos navegadores)
      if (window.location.reload) {
        window.location.reload(true);
      } else {
        window.location.reload();
      }
    }
  };

  const getStatusIcon = () => {
    if (status === "completed") {
      return <CheckCircleIcon style={{ color: "#4caf50", marginRight: 8 }} />;
    }
    if (status === "error") {
      return <ErrorIcon style={{ color: "#f44336", marginRight: 8 }} />;
    }
    return null;
  };

  const getStatusText = () => {
    if (status === "completed") {
      return "Atualização concluída com sucesso!";
    }
    if (status === "error") {
      return "Erro na atualização do sistema";
    }
    return "Atualização em andamento...";
  };

  const getLogColor = (type) => {
    switch (type) {
      case "success":
        return classes.logSuccess;
      case "error":
        return classes.logError;
      case "warning":
        return classes.logWarning;
      default:
        return classes.logInfo;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: classes.dialog
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {getStatusIcon()}
              <Typography variant="h6">Progresso - Atualização do Sistema</Typography>
            </Box>
            {status !== "running" && (
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Processo em tempo real
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress}
            className={classes.progressBar}
            color={status === "error" ? "secondary" : "primary"}
          />

          <Typography variant="body2" className={classes.statusText}>
            {progress}% - {getStatusText()}
          </Typography>

          <Box className={classes.tabContainer}>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Logs do Servidor" />
            </Tabs>
          </Box>

          <Box className={classes.logsContainer}>
            {logs.length === 0 ? (
              <Typography style={{ color: "#999" }}>
                Aguardando logs...
              </Typography>
            ) : (
              logs.map((log, index) => (
                <div
                  key={index}
                  className={`${classes.logEntry} ${getLogColor(log.type)}`}
                >
                  {log.message}
                </div>
              ))
            )}
            <div ref={logsEndRef} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="primary"
            variant="contained"
            disabled={status === "running"}
          >
            {status === "running" ? "Aguardando conclusão..." : "Fechar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Conclusão */}
      <Dialog
        open={showCompletionDialog}
        onClose={() => setShowCompletionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CheckCircleIcon style={{ color: "#4caf50", marginRight: 8, fontSize: 32 }} />
            <Typography variant="h6">Atualização Concluída!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            A atualização do sistema foi concluída com sucesso!
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Para garantir que todas as alterações sejam aplicadas corretamente, 
            recomendamos limpar o cache do navegador e recarregar a página.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowCompletionDialog(false);
              onClose();
            }}
            color="default"
          >
            Fechar
          </Button>
          <Button
            onClick={handleReloadWithCacheClear}
            color="primary"
            variant="contained"
            startIcon={<CheckCircleIcon />}
          >
            Limpar Cache e Recarregar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UpdateSystemProgress;
